// Co-brand logo for a TAP keychain batch: file → data URI, downsized in the
// browser so the row stays small (the server refuses anything over 200KB
// encoded). Ported from the V1 TapCreatePanel, minus its SVG bug: V1 base64-
// encoded a string that was ALREADY a data URL, so an SVG logo never rendered.
// Here an SVG is kept as the data URL the reader produced; PNG / JPG / WebP go
// through a canvas at most 600 x 300.

export const BRAND_MAX_W = 600;
export const BRAND_MAX_H = 300;
export const BRAND_MAX_FILE_BYTES = 2_000_000;
export const BRAND_MAX_ENCODED = 180_000;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read that image file.'));
    img.src = src;
  });
}

// Resolves to a data URI ready for branding_image_url, or throws with a
// message meant for the broker.
export async function readBrandImage(file) {
  if (!file) throw new Error('No file chosen.');
  if (!/^image\//.test(file.type)) throw new Error('That does not look like an image. PNG, JPG or SVG please.');
  if (file.size > BRAND_MAX_FILE_BYTES) throw new Error('File is over 2MB. Try a smaller image.');

  const raw = await readAsDataUrl(file);
  let out = raw;
  if (file.type !== 'image/svg+xml') {
    const img = await loadImage(raw);
    const scale = Math.min(1, BRAND_MAX_W / img.width, BRAND_MAX_H / img.height);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    // PNG keeps transparency (most logos); a photo-style JPG stays JPG.
    out = canvas.toDataURL(file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png', 0.9);
  }
  if (out.length > BRAND_MAX_ENCODED) throw new Error('Image is still too large after resizing. Try a simpler logo.');
  return out;
}
